import 'regenerator-runtime/runtime';
import { FormUtils, ValidationUtils } from '@fmr-pr103625/apexutils-common';
import { ApexLabelModal } from '@fmr-pr103625/apex-core';
import { apexFullName as CommonContent } from '@fmr-pr103625/apex-common-content';
import { html, property, unsafeCSS } from 'lit-element';
import type { TemplateResult } from 'lit-element';
import css from './component.scss';

enum PropValidMap {
  firstName = 'isFirstNameValid',
  middleName = 'isMiddleNameValid',
  lastName = 'isLastNameValid'
}

type NameType = 'firstName' | 'middleName' | 'lastName';

export default class ApxFullName extends ApexLabelModal {
  //#region LIFE CYCLE HOOKS
  public createRenderRoot(): this {
    return this;
  }

  public connectedCallback(): void {
    super.connectedCallback();
    // @ts-ignore
    window.ApexEventBus.addEventListener('apexValidate', this.validationEventHandler.bind(this));
  }

  public disconnectedCallback(): void {
    // @ts-ignore
    window.ApexEventBus.removeEventListener('apexValidate', this.validationEventHandler);
    super.disconnectedCallback();
  }

  protected validationEventHandler(): void {
    this.checkValidity();
  }

  public checkValidity(suppressErrors = false): void {
    // We need to ensure there is always a value - even just an empty string.
    // Some frameworks may make the value undefined if it's an empty string which will fail min requirements.
    this.middleName = this.middleName || '';
    this.validateComponent(suppressErrors);
  }
  //#endregion

  //#region RENDER
  public render(): TemplateResult {
    const {
      disableAutocomplete,
      firstNameLabel,
      groupLabel,
      idPrefix,
      lastNameLabel,
      middleNameLabel,
      readonly,
      hideMiddleName
    } = this;
    const enableBrowserAutocomplete = disableAutocomplete ? 'no' : 'on';
    let { firstName, lastName, middleName } = this;
    // if we don't manually make sure these values are an empty string,
    // if the value is undefined, it will render the string 'undefined' in the field
    firstName = firstName || '';
    middleName = middleName || '';
    lastName = lastName || '';

    let helpLink;
    let helpModal;
    if (this.helpBody) {
      helpLink = this.createHelpLink(idPrefix);
      helpModal = this.createHelpModal(idPrefix);
    }

    return html`
      ${readonly
        ? html`
            <div class="pvd-display-row">
              <div class="display-row-label ">
                <p>${groupLabel}</p>
              </div>
              <div class="display-row-content">
                <p data-pii="true">${firstName} ${hideMiddleName ? '' : middleName} ${lastName}</p>
              </div>
            </div>
          `
        : html`
            <div>
              <style>
                ${unsafeCSS(css)}
              </style>
              <div
                class="pvd-form-row form-row-full-name${hideMiddleName ? ' form-row-hide-middle-name' : ''}"
                role="group"
                aria-labelledby="${this.idPrefix}-mainLabel"
              >
                ${
                  helpModal && helpLink
                    ? this.createLabel(`${this.idPrefix}-mainLabel`, groupLabel, helpLink)
                    : html`<div class="form-row-label">
                        <label id="${this.idPrefix}-mainLabel"> ${groupLabel}</label>
                      </div> `
                }
                <label for="${idPrefix}-firstName"> ${firstNameLabel} </label>
                <input
                  value="${firstName}"
                  @change=${(e: Event): void => this.handleChange('firstName', e)}
                  @input=${(e: Event): void => this.handleInput('firstName', e)}
                  minlength=${this.validationRules.firstName.min}
                  maxlength=${this.validationRules.firstName.max}
                  id="${idPrefix}-firstName"
                  type="text"
                  class="pvd-input"
                  data-pii="true"
                  autocomplete="${enableBrowserAutocomplete}"
                />
                <pvd-system-message
                    id="${idPrefix}-firstName-error"
                    placement="inline"
                    type="alert"
                    class="hide-element"
                  ></pvd-system-message>
                ${
                  hideMiddleName
                    ? ''
                    : html`
                        <label for="${idPrefix}-middleName"> ${middleNameLabel} </label>
                        <input
                          value="${middleName}"
                          @change=${(e: Event): void => this.handleChange('middleName', e)}
                          @input=${(e: Event): void => this.handleInput('middleName', e)}
                          minlength=${this.validationRules.middleName.min}
                          maxlength=${this.validationRules.middleName.max}
                          id="${idPrefix}-middleName"
                          type="text"
                          class="pvd-input"
                          data-pii="true"
                          autocomplete="${enableBrowserAutocomplete}"
                        />
                        <pvd-system-message
                          id="${idPrefix}-middleName-error"
                          placement="inline"
                          type="alert"
                          class="hide-element"
                        ></pvd-system-message>
                      `
                }
                <label for="${idPrefix}-lastName"> ${lastNameLabel} </label>
                <input
                  value="${lastName}"
                  @change=${(e: Event): void => this.handleChange('lastName', e)}
                  @input=${(e: Event): void => this.handleInput('lastName', e)}
                  minlength=${this.validationRules.lastName.min}
                  maxlength=${this.validationRules.lastName.max}
                  id="${idPrefix}-lastName"
                  type="text"
                  class="pvd-input"
                  data-pii="true"
                  autocomplete="${enableBrowserAutocomplete}"
                />
                <pvd-system-message
                  id="${idPrefix}-lastName-error"
                  placement="inline"
                  type="alert"
                  class="hide-element"
                ></pvd-system-message>
                </div>
              </div>
            </div>
            ${helpModal || ``}
          `}
    `;
  }
  //#endregion

  //#region PROPERTIES
  @property({ type: String, attribute: 'id-prefix' })
  public idPrefix = 'name';

  @property({ type: Boolean, attribute: 'readonly' })
  public readonly = false;

  @property({ type: String, attribute: 'first-name', reflect: true })
  public firstName = '';

  @property({ type: String, attribute: 'first-name-label' })
  public firstNameLabel = CommonContent.fields.firstName.label;

  @property({ type: String, attribute: 'group-label' })
  public groupLabel = CommonContent.groupLabel;

  @property({ type: String, attribute: 'last-name', reflect: true })
  public lastName = '';

  @property({ type: String, attribute: 'last-name-label' })
  public lastNameLabel = CommonContent.fields.lastName.label;

  @property({ type: String, attribute: 'middle-name', reflect: true })
  public middleName = '';

  @property({ type: String, attribute: 'middle-name-label' })
  public middleNameLabel = CommonContent.fields.middleName.label;

  @property({ type: Boolean, attribute: 'hide-middle-name' })
  public hideMiddleName = false;

  @property({ type: Object, attribute: 'validation-rules' })
  public validationRules = {
    firstName: {
      required: CommonContent.fields.firstName.validationRules.isRequired,
      max: CommonContent.fields.firstName.validationRules.maxLength,
      min: CommonContent.fields.firstName.validationRules.minLength,
      regex: CommonContent.fields.firstName.validationRules.fieldRegex
    },
    lastName: {
      required: CommonContent.fields.lastName.validationRules.isRequired,
      max: CommonContent.fields.lastName.validationRules.maxLength,
      min: CommonContent.fields.lastName.validationRules.minLength,
      regex: CommonContent.fields.lastName.validationRules.fieldRegex
    },
    middleName: {
      required: CommonContent.fields.middleName.validationRules.isRequired,
      max: CommonContent.fields.middleName.validationRules.maxLength,
      min: CommonContent.fields.middleName.validationRules.minLength,
      regex: CommonContent.fields.middleName.validationRules.fieldRegex
    }
  };

  @property({ type: Object, attribute: 'validation-text' })
  public validationText = {
    firstName: {
      required: CommonContent.fields.firstName.errorMessages.isRequired,
      max: CommonContent.fields.firstName.errorMessages.maxLength,
      min: CommonContent.fields.firstName.errorMessages.minLength,
      regex: CommonContent.fields.firstName.errorMessages.fieldRegex
    },
    lastName: {
      required: CommonContent.fields.lastName.errorMessages.isRequired,
      max: CommonContent.fields.lastName.errorMessages.maxLength,
      min: CommonContent.fields.lastName.errorMessages.minLength,
      regex: CommonContent.fields.lastName.errorMessages.fieldRegex
    },
    middleName: {
      max: CommonContent.fields.middleName.errorMessages.maxLength,
      regex: CommonContent.fields.middleName.errorMessages.fieldRegex
    }
  };

  @property({ type: Object, reflect: true })
  public get value(): Record<string, string> {
    return this.getFullNameRecord();
  }

  public set value(value: Record<string, string>) {
    const oldValue = { ...this.value };
    this.firstName = value.firstName || '';
    this.middleName = value.middleName || '';
    this.lastName = value.lastName || '';
    this.requestUpdate('value', oldValue);
  }

  @property({ type: Boolean, attribute: 'disable-autocomplete' })
  public disableAutocomplete = false;

  public isFirstNameValid = false;

  public isMiddleNameValid = false;

  public isLastNameValid = false;

  public get valid() {
    return this.isFirstNameValid && this.isMiddleNameValid && this.isLastNameValid;
  }

  //#endregion

  //#region METHODS
  protected handleChange(prop: NameType, event: Event): void {
    if (event && event.target) {
      const value = (event.target as HTMLInputElement).value.trim();
      this.validateEntry(prop, value);
      this.updateValue(prop, value);
    }
  }

  protected handleInput(prop: string, event: Event): void {
    if (event && event.target) {
      this.updateValue(prop, (event.target as HTMLInputElement).value.trim());
    }
  }

  protected updateValue(prop: string, value: string): void {
    // @ts-ignore
    this[prop] = value;
    this.dispatchEvent(
      new CustomEvent('value-changed', {
        bubbles: true,
        composed: true,
        detail: {
          // @TODO remove fullname in favor of value later to keep components consistent
          fullname: this.getFullNameRecord(),
          value: this.getFullNameRecord(),
          valid: this.valid
        }
      })
    );
  }

  protected getFullNameRecord(): Record<string, string> {
    return {
      firstName: this.firstName || '',
      lastName: this.lastName || '',
      middleName: this.middleName || ''
    };
  }

  protected validateEntry(prop: NameType, value: string, suppressErrors = false): boolean {
    const key = PropValidMap[prop];
    this[key] = ['required', 'min', 'max', 'regex'].every((validator): boolean => {
      let propValid = false;
      // @ts-ignore
      const fieldText = this.validationText[prop][validator];
      // @ts-ignore
      const fieldRule = this.validationRules[prop][validator];

      if (!ValidationUtils.commonRules[validator](fieldRule, value)) {
        if (!suppressErrors) {
          this.errorMessage(prop, fieldText, true, 'true');
        }
      } else {
        propValid = true;
        this.errorMessage(prop, '', false, 'false');
      }
      return propValid;
    });
    return this[key];
  }

  protected validateComponent(suppressErrors = false): void {
    this.validateEntry('firstName', this.firstName, suppressErrors);
    this.validateEntry('middleName', this.middleName, suppressErrors);
    this.validateEntry('lastName', this.lastName, suppressErrors);
  }

  protected errorMessage(prop: string, message: string, showError: boolean, invalid: string): void {
    const errorElement = this.querySelector(`#${this.idPrefix}-${prop}-error`) as HTMLElement;
    if (errorElement !== null) {
      errorElement.setAttribute('message', message);
      FormUtils.errorDisplay(errorElement, showError);
    }
    const inputElement = this.querySelector(`#${this.idPrefix}-${prop}`) as HTMLElement;
    if (inputElement !== null) {
      if (invalid === 'true') {
        inputElement.setAttribute('aria-invalid', invalid);
        inputElement.setAttribute('aria-describedby', `${this.idPrefix}-${prop}-error`);
      } else {
        inputElement.removeAttribute('aria-invalid');
        inputElement.removeAttribute('aria-describedby');
      }
    }
  }
  //#endregion
}

// it is required to do it this way instead of using the customElements decorator
// as this component may be imported numerous times
if (!window.customElements.get('apx-full-name')) {
  window.customElements.define('apx-full-name', ApxFullName);
}
